/**
 * El botón «+» — cuatro cosas que le pasan al dinero de una persona.
 *
 *   Me entró dinero · Gasté · Moví dinero · Pagué algo
 *
 * Antes cada una vivía en una pantalla distinta y con nombre de base de datos
 * ("Nueva transacción", "Transferencia", "Abono a deuda", "Pagar tarjeta"), y
 * para elegir la correcta había que saber en qué colección iba a guardarse.
 *
 * Reglas de este componente:
 *  - una pregunta por pantalla;
 *  - la respuesta avanza sola (no hay que buscar "Siguiente");
 *  - "Gasté" no distingue cuenta de tarjeta: el usuario elige con qué pagó y
 *    el sistema sabe si eso es efectivo o deuda;
 *  - "Pagué algo" es una sola lista de lo que se debe: nada de elegir primero
 *    entre tarjeta, préstamo o factura.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { track, crearCronometro } from "@/lib/productTelemetry";
import { money, whenShort } from "@/lib/human";
import { humanErrorLine } from "@/lib/human";
import { useCategories } from "@/hooks/useCategories";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight, CheckCircle2,
  ChevronLeft, Loader2, Wallet, CreditCard, Banknote,
} from "lucide-react";

const VERBS = [
  { key: "income", label: "Me entró dinero", hint: "Sueldo, una venta, un reembolso", icon: ArrowDownToLine, tone: "emerald" },
  { key: "expense", label: "Gasté", hint: "Una compra, el súper, la gasolina", icon: ArrowUpFromLine, tone: "rose" },
  { key: "move", label: "Moví dinero", hint: "De una cuenta tuya a otra", icon: ArrowLeftRight, tone: "sky" },
  { key: "pay", label: "Pagué algo", hint: "La tarjeta, el alquiler, un préstamo", icon: CheckCircle2, tone: "amber" },
];

const TONE = {
  emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
  rose: "bg-rose-500/15 text-rose-300 border-rose-500/25",
  sky: "bg-sky-500/15 text-sky-300 border-sky-500/25",
  amber: "bg-amber-500/15 text-amber-300 border-amber-500/25",
};

/**
 * Qué le pasa de verdad al gasto del mes según lo que estés pagando. NO es lo
 * mismo, y decir lo mismo para los tres sería mentir en dos de cada tres:
 *
 *  - tarjeta: el gasto se contó cuando compraste; pagarla sólo mueve dinero
 *    desde tu cuenta hacia esa deuda.
 *  - préstamo: amortizas capital; baja tu efectivo y baja lo que debes por
 *    igual, así que no eres más pobre.
 *  - pago frecuente (luz, alquiler, Netflix): eso SÍ es consumo del mes.
 *
 * La regla vive en el servidor (ledger.movement_kind); esto sólo la explica.
 */
const PAY_HINT = {
  card: "Esto no cuenta como gasto del mes: el gasto fue cuando compraste con la tarjeta.",
  debt: "Esto no cuenta como gasto del mes: baja tu dinero y baja lo que debes, en partes iguales.",
  recurring: "Esto sí cuenta como gasto del mes.",
};


// Cómo salió el dinero de una cuenta. Sólo se pregunta cuando cambia algo:
// con tarjeta de crédito no hay impuesto, y de una cuenta de efectivo tampoco.
const PAY_WAYS = [
  { key: "transfer", label: "Por transferencia", hint: "Del banco a otra persona o negocio", icon: ArrowLeftRight },
  { key: "cash", label: "En efectivo", hint: "Saqué el dinero y lo pagué en mano", icon: Banknote },
];

// Motivos de ingreso en palabras normales. La lista de categorías del sistema
// tiene nombres heredados ("Inversión", "Freelance") que se mantienen debajo.
const INCOME_REASONS = [
  { label: "Sueldo", category: "Salario" },
  { label: "Una venta", category: "Freelance" },
  { label: "Mi negocio", category: "Freelance" },
  { label: "Me devolvieron algo", category: "Otros" },
  { label: "Otra cosa", category: "Otros" },
];

// Nombre de cada paso, para saber DÓNDE se atasca la gente. Van aparte de los
// pasos mismos para no ensuciar la definición de cada flujo; un test estático
// comprueba que las dos listas siguen teniendo la misma longitud, porque un
// mapa desincronizado señalaría el paso equivocado y eso es peor que no medir.
const STEP_KEYS = {
  income: ["amount", "destination", "category", "confirm"],
  expense: ["amount", "source", "pay_way", "category", "confirm"],
  move: ["source", "destination", "amount", "confirm"],
  pay: ["obligation", "amount", "source", "confirm"],
};

// El verbo es como lo dice el usuario; el flujo es como lo cuenta la telemetría.
const FLOW_OF = { income: "income", expense: "expense", move: "transfer", pay: "payment" };

const normCur = (currency) => String(currency || "").toUpperCase().replace(["RD", "$"].join(""), "DOP") || "DOP";

const isFeeFreePlatform = (account) => {
  const type = String(account?.type || "").toLowerCase();
  const country = String(account?.bank_country || "").toUpperCase();
  const name = String(account?.name || "").toLowerCase();
  return ["crypto", "fintech"].includes(type)
    || ["CRYPTO", "FINTECH"].includes(country)
    || ["binance", "redotpay", "redot pay"].some(provider => name.includes(provider));
};

// `rates[X]` usa la misma convención que GET /fx/current: unidades de la
// moneda base por una unidad de X. La conversión real se repite en el backend;
// ésta sólo enseña el impacto antes de guardar.
const convertFx = (value, from, to, fx) => {
  const amount = Number(value);
  if (!amount || from === to) return amount || 0;
  const base = normCur(fx?.base || "DOP");
  const rates = fx?.rates || {};
  const fromRate = from === base ? 1 : Number(rates[from]);
  const toRate = to === base ? 1 : Number(rates[to]);
  if (!fromRate || !toRate) return null;
  return (amount * fromRate) / toRate;
};

export default function QuickFlow({ open, onOpenChange, initialVerb = null, onDone }) {
  const { user } = useAuth();
  const { categories } = useCategories();
  const [verb, setVerb] = useState(initialVerb);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [cards, setCards] = useState([]);
  const [due, setDue] = useState([]);

  const [amount, setAmount] = useState("");
  const [source, setSource] = useState(null);   // {kind:'account'|'card', id, name, currency}
  const [target, setTarget] = useState(null);   // destino de "moví dinero"
  const [category, setCategory] = useState(null);
  const [obligation, setObligation] = useState(null);
  const [note, setNote] = useState("");
  const [spentCurrency, setSpentCurrency] = useState(null);
  const [currencyTouched, setCurrencyTouched] = useState(false);
  const [fx, setFx] = useState(null);
  // Cómo se pagó. Es lo que decide el impuesto de la Ley 30-26, y no se puede
  // adivinar: pagar al plomero por transferencia tributa, dárselo en efectivo
  // no. Sin declararlo el backend NO cobra, así que preguntarlo es la única
  // forma de que la app cuadre con el extracto del banco.
  const [payWay, setPayWay] = useState(null);
  const [tax, setTax] = useState(null);
  // Con qué se puede pagar ESTA obligación. Lo dice el servidor
  // (`GET /payment-methods?for=…`), no esta pantalla: la lista que se
  // construía aquí a mano fue la que dejó la Visa APAP fuera.
  const [payMethods, setPayMethods] = useState([]);

  // Medición. En refs y no en estado: cambiar esto no debe repintar nada, y en
  // un `useEffect` de cierre el estado ya vendría obsoleto.
  const verbRef = useRef(initialVerb);
  const crono = useRef(crearCronometro());
  const flujoVivo = useRef(null);     // flujo empezado y aún sin terminar
  const pasoActual = useRef(0);
  const huboImpuesto = useRef(false);

  const reset = () => {
    setVerb(initialVerb); setStep(0); setAmount(""); setSource(null);
    setTarget(null); setCategory(null); setObligation(null); setNote("");
    setPayWay(null); setTax(null); setPayMethods([]);
    setSpentCurrency(null); setCurrencyTouched(false); setFx(null);
    crono.current = crearCronometro();
    pasoActual.current = 0;
    huboImpuesto.current = false;
    flujoVivo.current = null;
  };

  const nombrePaso = (v, i) => STEP_KEYS[v]?.[i] || "unknown";

  const empezarFlujo = (v) => {
    const flujo = FLOW_OF[v];
    if (!flujo) return;
    flujoVivo.current = flujo;
    crono.current = crearCronometro();
    pasoActual.current = 0;
    track(`${flujo}_flow_started`, { screen: "quickflow", step: nombrePaso(v, 0) });
  };

  // Un flujo que se cierra sin guardar es la señal más valiosa que hay: dice
  // exactamente en qué pregunta se rindió alguien.
  const abandonarFlujo = () => {
    const flujo = flujoVivo.current;
    if (!flujo) return;
    track(`${flujo}_flow_abandoned`, {
      screen: "quickflow",
      step: nombrePaso(verbRef.current, pasoActual.current),
      duration_ms: crono.current.transcurrido(),
      back_presses: crono.current.atras(),
      cancelled: true,
    });
    if (huboImpuesto.current) track("tax_preview_cancelled", { screen: "quickflow" });
    flujoVivo.current = null;
  };

  /**
   * Cuánto impuesto lleva esto, según el servidor. NUNCA se calcula aquí: una
   * copia de la regla en JavaScript se desincronizaría el día que cambie la
   * tasa, y el usuario vería un número distinto del que le van a cobrar.
   */
  const previewTax = async ({ amount, account_id, payment_kind, operation, same_owner }) => {
    if (!amount || Number(amount) <= 0) return setTax(null);
    try {
      const { data } = await api.post("/tax/preview", {
        amount: Number(amount), account_id, payment_kind,
        operation: operation || "expense", same_owner: !!same_owner,
      });
      const hay = data?.taxable && data.tax > 0;
      setTax(hay ? data : null);
      if (hay) {
        huboImpuesto.current = true;
        // Se registra que lo vio, no el importe. Ver telemetry.py.
        track("tax_preview_seen", { screen: "quickflow" });
      }
    } catch { setTax(null); }
  };

  useEffect(() => {
    if (!open) return;
    reset();
    api.get("/accounts").then(r => setAccounts(r.data || [])).catch(() => {});
    api.get("/cards").then(r => setCards((r.data || []).filter(c => c.kind === "credit"))).catch(() => {});
    api.get("/fx/current").then(r => setFx(r.data || null)).catch(() => {});
    api.get("/obligations", { params: { days: 60 } })
      .then(r => setDue(r.data?.items || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialVerb]);

  const expenseCats = useMemo(
    () => categories.filter(c => c.type === "expense"), [categories]);

  // ---- Fuentes de pago: cuentas y tarjetas en la MISMA lista ---------------
  // El usuario no debe distinguir `account` de `card`. El sistema sí.
  const payWith = useMemo(() => [
    ...accounts.map(a => ({
      kind: "account", id: a.id, name: a.name, currency: a.currency,
      balance: Number(a.balance || 0),
      isCash: a.type === "cash",
      isFeeFreePlatform: isFeeFreePlatform(a),
      detail: money(a.balance, a.currency), icon: a.type === "cash" ? Banknote : Wallet,
    })),
    ...cards.map(c => ({
      kind: "card", id: c.id, name: `${c.name} ••••${c.last4}`, currency: c.currency,
      detail: "tarjeta de crédito", icon: CreditCard,
    })),
  ], [accounts, cards]);

  const accountsOnly = payWith.filter(s => s.kind === "account");

  const accountCurrency = normCur(source?.currency || "DOP");
  const localCurrency = normCur(user?.country_currency || "DOP");
  const autoCurrency = (() => {
    if (currencyTouched || verb !== "expense" || source?.kind !== "account") return null;
    if (!amount || accountCurrency === localCurrency) return null;
    const typed = Number(amount);
    if (!typed || typed <= source.balance) return null;
    const converted = convertFx(typed, localCurrency, accountCurrency, fx);
    return converted !== null && converted <= source.balance ? localCurrency : null;
  })();
  const effectiveSpentCurrency = normCur(spentCurrency || autoCurrency || accountCurrency);
  const convertedExpense = effectiveSpentCurrency === accountCurrency
    ? null : convertFx(amount, effectiveSpentCurrency, accountCurrency, fx);

  // El salto de paso y su medición van JUNTOS y fuera del updater de `setStep`.
  // Un updater debe ser puro: React lo ejecuta dos veces bajo StrictMode, y
  // contar los retrocesos ahí dentro los duplicaría. Un dato inflado es peor
  // que no tener dato, porque parece un hallazgo.
  const irA = (i) => { pasoActual.current = i; crono.current.reiniciar(); setStep(i); };

  const back = () => {
    crono.current.marcarAtras();
    if (step === 0 && !initialVerb) { abandonarFlujo(); setVerb(null); verbRef.current = null; return; }
    irA(Math.max(0, step - 1));
  };
  const next = () => irA(step + 1);

  const finish = async (fn, successMsg) => {
    setSaving(true);
    try {
      await fn();
      const flujo = flujoVivo.current;
      if (flujo) {
        track(`${flujo}_flow_completed`, {
          screen: "quickflow", step: "confirm",
          duration_ms: crono.current.transcurrido(),
          back_presses: crono.current.atras(),
        });
        if (huboImpuesto.current) track("tax_preview_confirmed", { screen: "quickflow" });
        // Marcado ANTES de cerrar: si no, el cierre lo contaría también como
        // abandono y el mismo flujo saldría empezado, terminado y abandonado.
        flujoVivo.current = null;
      }
      toast.success(successMsg);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      track("api_error_shown", {
        screen: "quickflow",
        step: nombrePaso(verbRef.current, pasoActual.current),
      });
      toast.error(humanErrorLine(e));
    } finally { setSaving(false); }
  };

  // ---- Guardados -----------------------------------------------------------
  const saveIncome = () => finish(() => api.post("/transactions", {
    account_id: source.id, type: "income", category: category.category || category.value,
    amount: Number(amount), description: note,
  }), "Listo, lo anoté");

  const saveExpense = () => finish(() => api.post("/transactions", {
    type: "expense", category: category.value, amount: Number(amount), description: note,
    currency: effectiveSpentCurrency,
    ...(source.kind === "card" ? { card_id: source.id } : { account_id: source.id, payment_kind: payWay }),
  }), source?.kind === "card" ? "Anotado en tu tarjeta" : "Listo, lo anoté");

  const saveMove = () => finish(() => api.post("/transfers", {
    from_account_id: source.id, to_account_id: target.id, amount: Number(amount),
    description: note || "Moví dinero",
  }), "Dinero movido");

  const savePay = () => finish(() => api.post(
    `/obligations/${obligation.kind}/${obligation.id}/pay`,
    // El método viene del servidor tal cual; esta pantalla no lo traduce.
    { amount: Number(amount), method: source.method,
      account_id: source.account_id, card_id: source.card_id },
  ), "Pago registrado");

  // ---- Definición de pasos por verbo ---------------------------------------
  const flows = {
    income: [
      { q: "¿Cuánto te entró?", render: () => <AmountStep value={amount} onChange={setAmount} onNext={next} currency={accounts[0]?.currency} /> },
      { q: "¿Dónde cayó?", render: () => <ChoiceList options={accountsOnly} onPick={(o) => { setSource(o); next(); }} emptyHint="Primero agrega una cuenta en Mi dinero." /> },
      { q: "¿De qué fue?", render: () => <ChoiceList options={INCOME_REASONS.map(r => ({ id: r.label, name: r.label, ...r }))} onPick={(o) => { setCategory(o); next(); }} /> },
      { q: "¿Todo bien?", render: () => <Confirm lines={[["Te entró", money(amount, source?.currency)], ["A", source?.name], ["De", category?.label]]} note={note} setNote={setNote} cta="Guardar" onGo={saveIncome} saving={saving} /> },
    ],
    expense: [
      { q: "¿Cuánto gastaste?", render: () => <AmountStep value={amount} onChange={setAmount} onNext={next} currency={accounts[0]?.currency} /> },
      {
        q: "¿Con qué pagaste?", render: () => (
          <ChoiceList options={payWith} onPick={(o) => {
            setSource(o);
            setSpentCurrency(null);
            setCurrencyTouched(false);
            // Tarjetas, efectivo y plataformas cripto/fintech están exentos de
            // impuesto bancario dominicano; no hay una pregunta adicional.
            if (o.kind === "card" || o.isCash || o.isFeeFreePlatform) {
              setPayWay(o.kind === "card" ? "card" : o.isFeeFreePlatform ? "platform" : "cash");
              irA(step + 2);
            }
            else next();
          }} emptyHint="Primero agrega una cuenta o una tarjeta en Mi dinero." />
        )
      },
      {
        q: "¿Cómo lo pagaste?", render: () => (
          <ChoiceList
            options={PAY_WAYS.map(w => ({ id: w.key, name: w.label, detail: w.hint, icon: w.icon }))}
            onPick={(o) => {
              setPayWay(o.id);
              previewTax({ amount, account_id: source?.id, payment_kind: o.id });
              next();
            }} />
        )
      },
      { q: "¿En qué fue?", render: () => <ChoiceList grid options={expenseCats.map(c => ({ id: c.id || c.value, name: c.value, ...c }))} onPick={(o) => { setCategory(o); next(); }} /> },
      {
        q: "¿Todo bien?", render: () => (
          <Confirm
            lines={[["Gastaste", money(amount, effectiveSpentCurrency)],
              ...(convertedExpense !== null ? [["Saldrá", money(convertedExpense, accountCurrency)]] : []),
              ["Con", source?.name], ["En", category?.value]]}
            tax={tax}
            extra={source?.kind === "account" && accountCurrency !== localCurrency ? (
              <ExpenseCurrencyPicker
                accountCurrency={accountCurrency}
                localCurrency={localCurrency}
                selected={effectiveSpentCurrency}
                converted={convertedExpense}
                autoSelected={!!autoCurrency && !currencyTouched}
                onPick={(currency) => {
                  setSpentCurrency(currency);
                  setCurrencyTouched(true);
                }} />
            ) : null}
            hint={source?.kind === "card"
              ? "No sale dinero de tus cuentas: se suma a lo que debes en esa tarjeta."
              : source?.isFeeFreePlatform
                ? "Esta plataforma no añade impuesto ni comisión bancaria dominicana; si cambia la divisa, verás sólo la conversión."
                : null}
            note={note} setNote={setNote} cta="Guardar" onGo={saveExpense} saving={saving} />
        )
      },
    ],
    move: [
      { q: "¿De dónde?", render: () => <ChoiceList options={accountsOnly} onPick={(o) => { setSource(o); next(); }} /> },
      { q: "¿Hacia dónde?", render: () => <ChoiceList options={accountsOnly.filter(a => a.id !== source?.id)} onPick={(o) => { setTarget(o); next(); }} emptyHint="Necesitas al menos dos cuentas para mover dinero." /> },
      { q: "¿Cuánto?", render: () => <AmountStep value={amount} onChange={setAmount} onNext={next} currency={source?.currency} /> },
      {
        q: "¿Todo bien?", render: () => (
          <Confirm
            lines={[["Moverás", money(amount, source?.currency)], ["De", source?.name], ["A", target?.name]]}
            hint="Esto no es un gasto ni un ingreso: tu dinero sigue siendo el mismo, sólo cambia de sitio."
            note={note} setNote={setNote} cta="Mover" onGo={saveMove} saving={saving} />
        )
      },
    ],
    pay: [
      {
        q: "¿Qué pagaste?", render: () => (
          <ChoiceList
            options={due.map(d => ({
              id: `${d.kind}:${d.id}`, name: d.name,
              detail: `${money(d.amount, d.currency)} · ${whenShort(d.due_date)}`,
              overdue: d.overdue, raw: d,
            }))}
            onPick={(o) => {
              setObligation(o.raw); setAmount(String(o.raw.amount));
              api.get("/payment-methods", { params: { for: o.raw.kind } })
                .then(r => setPayMethods(r.data?.methods || []))
                .catch(() => setPayMethods([]));
              next();
            }}
            emptyHint="No tienes nada pendiente ahora mismo." />
        )
      },
      {
        q: "¿Cuánto pagaste?", render: () => (
          <AmountStep
            value={amount} onChange={setAmount} onNext={next}
            currency={obligation?.currency}
            // Lo que toca resolver AHORA es el importe de ESTA obligación — que
            // con tramos es la parte que vence, no el mes entero.
            dueNow={obligation?.amount}
            hint={obligation?.installment_day
              ? `Es la parte del ${obligation.installment_day}. Del mes te faltan ${money(obligation.month_remaining, obligation.currency)}.`
              : `Debías ${money(obligation?.amount, obligation?.currency)}. Puedes pagar menos si quieres.`} />
        )
      },
      {
        q: "¿Desde dónde pagaste?", render: () => (
          <ChoiceList
            options={payMethods.map(m => ({
              id: m.id, name: m.name,
              detail: m.kind === "card" ? "tarjeta de crédito"
                : m.balance != null ? money(m.balance, m.currency) : m.currency,
              icon: m.kind === "card" ? CreditCard : m.kind === "cash" ? Banknote : Wallet,
              disabled: !m.allowed, disabledHint: m.reason, method: m,
            }))}
            emptyHint="No hay con qué pagar esto todavía. Agrega una cuenta en Mi dinero."
            onPick={(o) => {
              const m = o.method;
              setSource({ kind: m.kind, id: m.id, name: m.name, currency: m.currency,
                          isCash: m.kind === "cash", method: m.method,
                          account_id: m.account_id, card_id: m.card_id });
              // Con tarjeta no hay impuesto de transferencia: el banco no mueve
              // dinero, la deuda de la tarjeta sube.
              if (m.kind === "card") setTax(null);
              else previewTax({
                amount, account_id: m.account_id,
                payment_kind: m.kind === "cash" ? "cash" : "transfer",
                operation: obligation?.kind === "card" ? "card_payment"
                  : obligation?.kind === "debt" ? "debt_payment" : "expense",
              });
              next();
            }} />
        )
      },
      {
        q: "¿Todo bien?", render: () => (
          <Confirm
            lines={[["Pagaste", money(amount, obligation?.currency)], ["De", obligation?.name], ["Desde", source?.name]]}
            tax={tax}
            hint={PAY_HINT[obligation?.kind]}
            note={note} setNote={setNote} cta="Guardar" onGo={savePay} saving={saving} />
        )
      },
    ],
  };

  const steps = verb ? flows[verb] : null;
  const current = steps?.[step];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) abandonarFlujo(); onOpenChange(v); }}>
      <DialogContent
        className="bg-[#15141b] border-white/10 text-white sm:max-w-md p-0 gap-0 max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="quick-flow">
        <div className="flex items-center gap-2 px-4 pt-4 pb-2 shrink-0">
          {(verb && (step > 0 || !initialVerb)) && (
            <button onClick={back} data-testid="quick-flow-back" aria-label="Volver"
              className="p-2 -ml-2 rounded-xl text-slate-300 hover:bg-white/10 min-h-11 min-w-11 flex items-center justify-center">
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          <DialogTitle className="text-lg font-bold text-white" data-testid="quick-flow-question">
            {verb ? current?.q : "¿Qué pasó?"}
          </DialogTitle>
        </div>
        <DialogDescription className="sr-only">
          Registra lo que pasó con tu dinero en unos pocos pasos.
        </DialogDescription>

        {verb && steps.length > 1 && (
          <div className="flex gap-1.5 px-4 pb-3 shrink-0" aria-hidden="true">
            {steps.map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-violet-400" : "bg-white/10"}`} />
            ))}
          </div>
        )}

        <div className="px-4 pb-5 overflow-y-auto">
          {!verb ? (
            <div className="space-y-2.5">
              {VERBS.map(v => (
                <button key={v.key} onClick={() => { setVerb(v.key); verbRef.current = v.key; setStep(0); empezarFlujo(v.key); }}
                  data-testid={`quick-verb-${v.key}`}
                  className="w-full flex items-center gap-3.5 p-4 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-left min-h-16 transition-colors">
                  <span className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 ${TONE[v.tone]}`}>
                    <v.icon className="w-5 h-5" strokeWidth={2} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-base font-semibold text-white">{v.label}</span>
                    <span className="block text-[13px] text-slate-400 truncate">{v.hint}</span>
                  </span>
                </button>
              ))}
            </div>
          ) : current?.render()}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */

function AmountStep({ value, onChange, onNext, currency = "DOP", hint, dueNow }) {
  const symbol = money(0, currency).replace(/[\d.,]/g, "");
  const ok = value !== "" && Number(value) > 0;
  // «Pagar completo» significa lo que toca resolver AHORA, no todo el mes ni
  // toda la deuda. Si Doméstica tiene 2,000 vencidos y 6,000 que aún no vencen,
  // el botón paga 2,000: adelantar la parte de fin de mes sin que se vea sería
  // sacar dinero que nadie mandó sacar. Adelantarla sigue siendo posible por
  // «otro monto», y entonces se dice que se está pagando de más.
  const [modo, setModo] = useState(dueNow ? null : "libre");

  if (dueNow && modo === null) {
    return (
      <div className="space-y-2.5" data-testid="quick-amount-choice">
        <button onClick={() => { onChange(String(dueNow)); setModo("completo"); onNext(); }}
          data-testid="quick-pay-full"
          className="w-full min-h-14 rounded-2xl bg-white text-[#15141b] font-bold text-[16px]">
          Pagar {money(dueNow, currency)}
        </button>
        <button onClick={() => setModo("libre")} data-testid="quick-pay-other"
          className="w-full min-h-14 rounded-2xl bg-white/[0.06] border border-white/10 text-white font-semibold">
          Escribir otro monto
        </button>
        {hint && <p className="pt-1 text-[13px] text-slate-400">{hint}</p>}
      </div>
    );
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (ok) onNext(); }} className="space-y-4">
      <label className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-2xl px-4 py-4 focus-within:border-violet-400">
        <span className="text-2xl font-semibold text-slate-400 shrink-0">{symbol}</span>
        {/* El foco entra solo en el campo: es la única pregunta de la pantalla
            y en el teléfono ahorra un toque. El lector de pantalla anuncia la
            etiqueta al recibirlo. */}
        <input
          autoFocus type="number" inputMode="decimal" step="0.01" min="0.01"
          value={value} onChange={(e) => onChange(e.target.value)} placeholder="0"
          aria-label={`Monto en ${currency}`} data-testid="quick-amount"
          className="w-full bg-transparent text-3xl font-bold text-white outline-none tabular-nums" />
      </label>
      {/* Qué va a pasar con lo que escribió, ANTES de confirmar. Un abono sin
          esta línea deja a la persona sin saber si acaba de saldar algo o no. */}
      {dueNow && ok && Number(value) < dueNow && (
        <p className="text-[13px] text-amber-300" data-testid="quick-amount-partial">
          Quedarán {money(dueNow - Number(value), currency)} por pagar.
        </p>
      )}
      {dueNow && ok && Number(value) > dueNow && (
        <p className="text-[13px] text-amber-300" data-testid="quick-amount-over">
          Solo faltan {money(dueNow, currency)}; el resto adelanta lo que viene.
        </p>
      )}
      {hint && <p className="text-[13px] text-slate-400">{hint}</p>}
      <button type="submit" disabled={!ok} data-testid="quick-amount-next"
        className="w-full min-h-12 rounded-2xl bg-white text-[#15141b] font-bold disabled:opacity-30 disabled:cursor-not-allowed">
        Seguir
      </button>
    </form>
  );
}

function ChoiceList({ options, onPick, emptyHint, grid = false }) {
  if (!options.length) {
    return (
      <p className="text-[15px] text-slate-400 py-6 text-center" data-testid="quick-choice-empty">
        {emptyHint || "No hay nada para elegir todavía."}
      </p>
    );
  }
  return (
    <div className={grid ? "grid grid-cols-2 gap-2.5" : "space-y-2"} data-testid="quick-choice-list">
      {options.map(o => (
        /* Una opción no permitida se enseña apagada y CON su motivo. Ocultarla
           dejaría «¿y por qué no puedo pagar con la Visa?» sin respuesta en
           pantalla, que es como se aprende a desconfiar de una app. */
        <button key={o.id} onClick={() => !o.disabled && onPick(o)}
          disabled={!!o.disabled} aria-disabled={!!o.disabled}
          data-testid={`quick-choice-${o.id}`}
          className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border text-left min-h-14 transition-colors ${
            o.disabled
              ? "bg-white/[0.02] border-white/5 opacity-60 cursor-not-allowed"
              : o.overdue
                ? "bg-rose-500/10 border-rose-500/30 hover:bg-rose-500/15"
                : "bg-white/[0.04] border-white/10 hover:bg-white/[0.08]"}`}>
          {o.icon && <o.icon className="w-5 h-5 text-slate-300 shrink-0" strokeWidth={2} />}
          <span className="min-w-0 flex-1">
            {/* En rejilla (categorías) el nombre puede ocupar dos líneas: cortar
                "Entretenimiento" a "Entretenimie…" obliga a adivinar. */}
            <span className={`block text-[15px] font-semibold text-white ${grid ? "leading-tight" : "truncate"}`}>{o.name}</span>
            {o.disabled && o.disabledHint
              ? <span className="block text-[12px] text-amber-300/90">{o.disabledHint}</span>
              : o.detail && <span className="block text-[12px] text-slate-400 truncate">{o.detail}</span>}
          </span>
        </button>
      ))}
    </div>
  );
}

function Confirm({ lines, hint, note, setNote, cta, onGo, saving, tax, extra }) {
  const [showNote, setShowNote] = useState(false);
  return (
    <div className="space-y-4">
      <dl className="rounded-2xl bg-white/[0.04] border border-white/10 divide-y divide-white/5">
        {lines.filter(([, v]) => v).map(([k, v], i) => (
          <div key={k} className="flex items-baseline justify-between gap-4 px-4 py-3">
            <dt className="text-[13px] text-slate-400 shrink-0">{k}</dt>
            <dd className={`text-right font-semibold text-white truncate ${i === 0 ? "text-xl tabular-nums" : "text-[15px]"}`}>{v}</dd>
          </div>
        ))}

        {/* El impuesto de la Ley 30-26, en la misma tabla y antes de confirmar.
            Si no lo hay, no aparece: una línea de RD$0 sólo añade ruido. El
            importe lo calcula el servidor — aquí no vive ninguna regla fiscal. */}
        {tax && (
          <>
            <div className="flex items-baseline justify-between gap-4 px-4 py-3"
              data-testid="quick-tax-line">
              <dt className="text-[13px] text-slate-400 shrink-0">Impuesto bancario</dt>
              <dd className="text-right text-[15px] font-semibold text-amber-300 tabular-nums">
                {money(tax.tax, tax.currency)}
              </dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 px-4 py-3 bg-white/[0.03]">
              <dt className="text-[13px] font-semibold text-white shrink-0">Total que saldrá</dt>
              <dd className="text-right text-lg font-bold text-white tabular-nums"
                data-testid="quick-tax-total">
                {money(tax.total_out, tax.currency)}
              </dd>
            </div>
          </>
        )}
      </dl>

      {extra}

      {tax && (
        <p className="text-[13px] text-slate-400 leading-relaxed" data-testid="quick-tax-explainer">
          {tax.explanation}
        </p>
      )}

      {hint && (
        <p className="text-[13px] text-slate-300 bg-white/[0.03] border border-white/10 rounded-xl px-3.5 py-3 leading-relaxed"
          data-testid="quick-confirm-hint">{hint}</p>
      )}

      {showNote ? (
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Una nota (opcional)"
          aria-label="Nota opcional" data-testid="quick-note"
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-3 text-white text-[15px] min-h-12" />
      ) : (
        <button type="button" onClick={() => setShowNote(true)} data-testid="quick-add-note"
          className="text-[13px] text-slate-400 hover:text-white underline underline-offset-4 min-h-11">
          Agregar una nota
        </button>
      )}

      <button onClick={onGo} disabled={saving} data-testid="quick-confirm"
        className="w-full min-h-13 py-3.5 rounded-2xl bg-white text-[#15141b] font-bold flex items-center justify-center gap-2 disabled:opacity-50">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : cta}
      </button>
    </div>
  );
}

function ExpenseCurrencyPicker({
  accountCurrency, localCurrency, selected, converted, autoSelected, onPick,
}) {
  return (
    <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-3.5 space-y-2.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[13px] text-slate-300">Moneda del gasto</span>
        <div className="flex gap-1" data-testid="tx-currency-picker">
          {[accountCurrency, localCurrency].map(currency => (
            <button key={currency} type="button" onClick={() => onPick(currency)}
              aria-pressed={selected === currency}
              data-testid={`tx-currency-${currency}`}
              className={`px-3 min-h-9 rounded-xl text-[12px] font-bold transition-colors ${
                selected === currency
                  ? "bg-white text-[#15141b]"
                  : "bg-white/[0.06] text-slate-300 hover:bg-white/[0.1]"
              }`}>
              {currency}
            </button>
          ))}
        </div>
      </div>
      {converted !== null && (
        <p className="text-[12px] text-slate-400" data-testid="tx-fx-preview">
          {autoSelected ? `Lo leí como ${selected}: en ${accountCurrency} no te alcanzaría. ` : ""}
          Saldrán aproximadamente {money(converted, accountCurrency)} de la cuenta, con la tasa de hoy.
        </p>
      )}
      {selected !== accountCurrency && converted === null && (
        <p className="text-[12px] text-amber-300" data-testid="tx-fx-missing">
          No tengo la tasa {selected}→{accountCurrency} ahora mismo; no se guardará sin convertir.
        </p>
      )}
    </div>
  );
}
